import { useEffect, useState } from 'react';
import { Col, Form, Row, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Thinning } from '../interfaces';
import { Store } from '../../../state/store.interfaces';
import { validateNumber } from '../../../utils/validations';
import { LrvForm } from '../../../common/components/LrvForm/LrvForm';
import * as optimalHarvesPointSlice from '../optimalHarvestPointSlice';
import { useFormErrorFields } from '../../../hooks/useFormErrorFields';
import { LrvModal } from '../../../common/components/LrvModal/LrvModal';
import { LrvSelectV2 } from '../../../common/components/LrvSelectV2/LrvSelectV2';
import { roundTwoDecimals, weightUnits, weightUnitsByCompany } from '../../../config/commons';
import { LrvInputNumberV2 } from '../../../common/components/LrvInputNumberV2/LrvInputNumberV2';
import { convertKilogramsToPounds, convertPoundsToKilograms } from '../../../helpers/stocking.helpers';

import { getAnimals, getAverageWeight, getMaxBiomass, getTailIncome, getWasteIncome, getWholeIncome } from './helpers';

const { Option } = Select;

interface Props {
  theme?: 'dark' | 'light';
}

export const EditThinningForm = (props: Props) => {
  const { theme = 'dark' } = props;
  const [t] = useTranslation();
  const dispatch = useDispatch();

  const [form] = Form.useForm();
  const { errorFields, resetErrorFields, extractAndSetErrorFields } = useFormErrorFields({ form });

  const { company } = useSelector((state: Store) => state.header);
  const { allPackers: packers, firstStage, lastStage, allPredictions, thinning, filters } = useSelector((state: Store) => state.optimalHarvestPoint);
  const { showEditModal, isUpdating, selectedThinning } = thinning;

  const {
    selectedStocking,
  } = filters;

  const [day, setDay] = useState<number | string | null | undefined>();
  const [biomassKg, setBiomassKg] = useState<number | string | null | undefined>();
  const [biomassLb, setBiomassLb] = useState<number | string | null | undefined>();
  const [packerId, setPackerId] = useState<string>('');

  useEffect(() => {
    if (!selectedThinning?._id || !showEditModal) {
      return;
    }

    setDay(selectedThinning.day);
    setBiomassKg(selectedThinning.biomass);
    setPackerId(selectedThinning.packerId);

    const pounds = convertKilogramsToPounds(selectedThinning.biomass);
    setBiomassLb(pounds);

    form.setFieldsValue({
      day: selectedThinning.day,
      biomass: selectedThinning.biomass,
    });

  }, [selectedThinning, showEditModal]);

  const closeModal = () => {
    form.resetFields();
    resetErrorFields();
    
    setDay('');
    setBiomassKg('');
    setBiomassLb('');
    setPackerId('');
    
    dispatch(optimalHarvesPointSlice.setShowEditThinningModal(false));
    dispatch(optimalHarvesPointSlice.setSelectedThinning(undefined));
  };

  const averageWeight = getAverageWeight({ day: Number(day), allPredictions });
  const animals = getAnimals({ day: Number(day), allPredictions, biomassKg: Number(biomassKg) });
  const tailIncome = getTailIncome({ day: Number(day), allPredictions, biomassKg: Number(biomassKg), packerId });
  const wasteIncome = getWasteIncome({ day: Number(day), allPredictions, biomassKg: Number(biomassKg), packerId });
  const wholeIncome = getWholeIncome({ day: Number(day), allPredictions, biomassKg: Number(biomassKg), packerId });

  const renderDayInput = () => {
    const hasError = errorFields.includes('day');

    return (
      <Form.Item
        name='day'
        rules={[{ required: true, message: t('common.requiredField') }]}
      >
        <LrvInputNumberV2
          label={t('thinnings.day')}
          theme={theme}
          value={day}
          onChange={setDay}
          min={firstStage}
          max={lastStage}
          hasError={hasError}
          autoFocus
        />
      </Form.Item>
    );
  };

  const renderAverageWeight = () => {
    return (
      <Form.Item
        required={false}
      >
        <LrvInputNumberV2
          label={`${t('thinnings.averageWeight')} ${weightUnits.G}`}
          theme={theme}
          value={roundTwoDecimals(averageWeight)}
          readOnly
        />
      </Form.Item>
    );
  };

  const renderBiomassKgInput = () => {
    if (company.weightUnit !== weightUnitsByCompany.KILOGRAM) {
      return;
    }

    const hasError = errorFields.includes('biomass');
    const label = `${t('thinnings.biomass')} ${weightUnits.KG}`;

    return (
      <Form.Item
        name='biomass'
        rules={[() => ({
          validator (rule, value) {
            const max = getMaxBiomass({ day: Number(day), weightUnit: company.weightUnit, allPredictions });
            return validateNumber(value, false, 1, max, t('thinnings.maxBiomass'));
          }
        })]}
      >
        <LrvInputNumberV2
          label={label}
          theme={theme}
          value={biomassKg}
          onChange={(value) => {
            setBiomassKg(value);
            
            const pounds = convertKilogramsToPounds(Number(value));
            setBiomassLb(pounds);
          }}
          hasError={hasError}
          min={0}
        />
      </Form.Item>
    );
  };

  const renderBiomassLbInput = () => {
    if (company.weightUnit !== weightUnitsByCompany.POUND) {
      return;
    }

    const hasError = errorFields.includes('biomass');
    const label = `${t('thinnings.biomass')} ${weightUnits.LB}`;

    return (
      <Form.Item
        name='biomass'
        rules={[() => ({
          validator (rule, value) {
            const max = getMaxBiomass({ day: Number(day), weightUnit: company.weightUnit, allPredictions });
            return validateNumber(value, false, 1, max, t('thinnings.maxBiomass'));
          }
        })]}
      >
        <LrvInputNumberV2
          label={label}
          theme={theme}
          value={biomassLb}
          onChange={(value) => {
            setBiomassLb(value);
            
            const kilograms = convertPoundsToKilograms(Number(value));
            setBiomassKg(kilograms);
          }}
          hasError={hasError}
          min={0}
        />
      </Form.Item>
    );
  };

  const renderAnimals = () => {
    return (
      <Form.Item
        required={false}
      >
        <LrvInputNumberV2
          label={t('thinnings.animals')}
          theme={theme}
          value={animals}
          readOnly
        />
      </Form.Item>
    );
  };

  const renderPackerDropdown = () => {
    return (
      <Form.Item
        required={false}
      >
        <LrvSelectV2
          theme={theme}
          showSearch
          value={packerId}
          label={t('thinnings.packer')}
          onChange={setPackerId}
        >
          {packers.map((item, index: number) =>
            <Option key={index} value={item.packerId}>{item.packerName}</Option>
          )}
        </LrvSelectV2>
      </Form.Item>
    );
  };

  const renderTailIncome = () => {
    return (
      <Form.Item
        required={false}
      >
        <LrvInputNumberV2
          label={t('thinnings.incomes.tail')}
          theme={theme}
          value={roundTwoDecimals(tailIncome)}
          readOnly
        />
      </Form.Item>
    );
  };

  const renderWholeIncome = () => {
    return (
      <Form.Item
        required={false}
      >
        <LrvInputNumberV2
          label={t('thinnings.incomes.whole')}
          theme={theme}
          value={roundTwoDecimals(wholeIncome)}
          readOnly
        />
      </Form.Item>
    );
  };

  const renderWasteIncome = () => {
    return (
      <Form.Item
        required={false}
      >
        <LrvInputNumberV2
          label={t('thinnings.incomes.waste')}
          theme={theme}
          value={roundTwoDecimals(wasteIncome)}
          readOnly
        />
      </Form.Item>
    );
  };

  const disableThinningForm = () => {
    return !day || !biomassKg || !packerId || !selectedThinning?._id;
  };

  const onUpdateThinning = () => {
    if (disableThinningForm()) {
      return;
    }

    const body: Thinning = {
      _id: selectedThinning?._id,
      day: Number(day),
      biomass: Number(biomassKg),
      averageWeight,
      animals,
      packerId,
      stockingId: selectedStocking._id,
      companyId: selectedStocking.companyId,
    };

    dispatch(optimalHarvesPointSlice.updateThinning({ body, onSuccess: closeModal }));
  };

  return (
    <LrvModal
      theme={theme}
      title={t('thinnings.edit')}
      open={showEditModal}
      destroyOnClose={true}
      okButtonProps={{
        id: 'submit-edit-thinning',
        htmlType: 'submit',
        form: 'form',
        loading: isUpdating,
        disabled: disableThinningForm(),
      }}
      onOk={onUpdateThinning}
      okText={t('actions.edit')}
      cancelText={t('actions.cancel')}
      onCancel={closeModal}
    >
      <LrvForm
        theme={theme}
        form={form}
        name='form'
        id='new-thinning-form'
        layout='vertical'
        onFieldsChange={extractAndSetErrorFields}
      >
        {renderDayInput()}
        {renderBiomassKgInput()}
        {renderBiomassLbInput()}
        {renderPackerDropdown()}

        <Row gutter={16} >
          <Col span={12}>
            {renderAverageWeight()}
          </Col>
          <Col span={12}>
            {renderAnimals()}
          </Col>
        </Row>

        <Row gutter={16} >
          <Col span={12}>
            {renderTailIncome()}
          </Col>
          <Col span={12}>
            {renderWholeIncome()}
          </Col>
        </Row>

        <Row gutter={16} >
          <Col span={12}>
            {renderWasteIncome()}
          </Col>
        </Row>
      </LrvForm>
    </LrvModal>
  );
};